import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createCannedMessage,
    updateCannedMessage
} from '../../redux/features/cannedMessages/cannedMessageSlice';
import './CannedMessageModal.css';

const CannedMessageModal = ({ message, categories, onClose }) => {
    const dispatch = useDispatch();
    const { isLoading } = useSelector((state) => state.cannedMessages);

    const [formData, setFormData] = useState({
        title: '',
        category: '',
        description: '',
        status: 'active'
    });

    const [errors, setErrors] = useState({});

    // Filter active categories for dropdown
    const activeCategories = categories.filter(cat => cat.status === 'active');

    useEffect(() => {
        if (message) {
            setFormData({
                title: message.title,
                category: message.category?._id || '',
                description: message.description,
                status: message.status || 'active'
            });
        } else if (activeCategories.length > 0) {
            // Set first active category as default for new messages
            setFormData(prev => ({
                ...prev,
                category: activeCategories[0]._id
            }));
        }
    }, [message, activeCategories.length]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length > 100) {
            newErrors.title = 'Title must be less than 100 characters';
        }
        if (!formData.category) {
            newErrors.category = 'Category is required';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.length > 500) {
            newErrors.description = 'Description must be less than 500 characters';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            if (message) {
                await dispatch(updateCannedMessage({ id: message._id, messageData: formData })).unwrap();
            } else {
                await dispatch(createCannedMessage(formData)).unwrap();
            }
            onClose();
        } catch (error) {
            console.error('Failed to save message:', error);
        }
    };

    if (activeCategories.length === 0 && !message) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>No Active Categories</h3>
                        <button className="modal-close" onClick={onClose}>
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                    <div style={{ padding: '24px', textAlign: 'center' }}>
                        <i className="fa fa-exclamation-triangle" style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '16px' }}></i>
                        <p>Please create at least one active category before creating messages.</p>
                        <button onClick={onClose} className="btn-save" style={{ marginTop: '16px' }}>
                            OK
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{message ? 'Edit Canned Message' : 'Create Canned Message'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="title">
                            Title <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter message title"
                            className={errors.title ? 'error' : ''}
                            maxLength={100}
                        />
                        {errors.title && <span className="error-text">{errors.title}</span>}
                        <span className="char-count">{formData.title.length}/100</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="category">
                            Category <span className="required">*</span>
                        </label>
                        <select
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            className={errors.category ? 'error' : ''}
                        >
                            <option value="">Select a category</option>
                            {activeCategories.map(cat => (
                                <option key={cat._id} value={cat._id}>
                                    {cat.title}
                                </option>
                            ))}
                        </select>
                        {errors.category && <span className="error-text">{errors.category}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">
                            Description <span className="required">*</span>
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Enter message description"
                            rows={6}
                            className={errors.description ? 'error' : ''}
                            maxLength={500}
                        />
                        {errors.description && <span className="error-text">{errors.description}</span>}
                        <span className="char-count">{formData.description.length}/500</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <div className="status-toggle">
                            <button
                                type="button"
                                className={`status-btn ${formData.status === 'active' ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                            >
                                <i className="fa fa-check-circle"></i> Active
                            </button>
                            <button
                                type="button"
                                className={`status-btn ${formData.status === 'inactive' ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, status: 'inactive' }))}
                            >
                                <i className="fa fa-ban"></i> Inactive
                            </button>
                        </div>
                        <small className="help-text">
                            Inactive messages will be hidden from the message picker
                        </small>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-save" disabled={isLoading}>
                            {isLoading ? 'Saving...' : message ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CannedMessageModal;
